/**
 * The app's language codes, and the one place they meet the backend's (AL-1843).
 *
 * i18next derives plural categories from the language code through
 * Intl.PluralRules, so the codes it sees must be BCP 47. The backend stores its
 * own (User::LANGUAGE_*): `en`, `pt`, `br`, `es` — and `br` is Breton to Intl,
 * which read 21 as singular and sent Brazilian accounts to European Portuguese
 * on 2 and 3. Inside the app the codes are BCP 47; the backend's appear only
 * where a value crosses to or from the API, through the two functions below.
 *
 * The locale folders keep their old names (`src/locales/br/…`): a folder name
 * is not a language code, and renaming them would only churn every import.
 */

export const APP_LANGUAGES = ['en', 'pt-PT', 'pt-BR', 'es'] as const;

export type AppLanguage = (typeof APP_LANGUAGES)[number];

export const BACKEND_LANGUAGES = ['en', 'pt', 'br', 'es'] as const;

export type BackendLanguage = (typeof BACKEND_LANGUAGES)[number];

const BACKEND_TO_APP: Readonly<Record<BackendLanguage, AppLanguage>> = {
  en: 'en',
  pt: 'pt-PT',
  br: 'pt-BR',
  es: 'es',
};

const APP_TO_BACKEND: Readonly<Record<AppLanguage, BackendLanguage>> = {
  en: 'en',
  'pt-PT': 'pt',
  'pt-BR': 'br',
  es: 'es',
};

/** Where each language's bundle lives under `src/locales/`. */
export const LOCALE_FOLDER: Readonly<Record<AppLanguage, string>> = {
  en: 'en',
  'pt-PT': 'pt',
  'pt-BR': 'br',
  es: 'es',
};

function isAppLanguage(code: string): code is AppLanguage {
  return (APP_LANGUAGES as readonly string[]).includes(code);
}

function isBackendLanguage(code: string): code is BackendLanguage {
  return (BACKEND_LANGUAGES as readonly string[]).includes(code);
}

/**
 * A backend code (or an app code already) as the app's language, or null when
 * it is neither. Matching is case-insensitive on the region (`pt-br` → `pt-BR`).
 */
export function fromBackendLanguage(code: string | null | undefined): AppLanguage | null {
  if (!code) return null;
  if (isBackendLanguage(code)) return BACKEND_TO_APP[code];
  const match = APP_LANGUAGES.find(language => language.toLowerCase() === code.toLowerCase());
  return match ?? null;
}

/** The app's language as the backend stores it. Unknown codes fall back to `en`. */
export function toBackendLanguage(code: string): BackendLanguage {
  if (isAppLanguage(code)) return APP_TO_BACKEND[code];
  if (isBackendLanguage(code)) return code;
  return 'en';
}

/** The key LanguageDetector caches the choice under. */
export const LANGUAGE_STORAGE_KEY = 'i18nextLng';

/**
 * Rewrites a language cached before AL-1843 (`br`, `pt`) to its BCP 47 code.
 *
 * Runs before i18next initialises: left alone, a cached `br` is not a
 * supported code any more, and the detector would quietly move every Brazilian
 * user to English on their next visit. Storage that throws (private mode,
 * blocked site data) is left as it is — the detector then falls back to the
 * querystring and the browser language, as it would have anyway.
 */
export function migrateStoredLanguage(storage: Pick<Storage, 'getItem' | 'setItem'> | undefined): void {
  if (!storage) return;
  try {
    const stored = storage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && isBackendLanguage(stored) && !isAppLanguage(stored)) {
      storage.setItem(LANGUAGE_STORAGE_KEY, BACKEND_TO_APP[stored]);
    }
  } catch {
    // Unreadable storage: nothing to migrate.
  }
}
